import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const PermissionManagement = () => {
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});

  // Definición de recursos y acciones disponibles
  const resources = {
    users: {
      name: 'Usuarios',
      actions: {
        create: 'Crear',
        read: 'Ver',
        update: 'Editar',
        delete: 'Eliminar'
      }
    },
    socios: {
      name: 'Socios',
      actions: {
        create: 'Crear',
        read: 'Ver',
        update: 'Editar',
        delete: 'Eliminar',
        viewAll: 'Ver Todos'
      }
    },
    reports: {
      name: 'Reportes',
      actions: {
        view: 'Ver',
        export: 'Exportar'
      }
    },
    system: {
      name: 'Sistema',
      actions: {
        settings: 'Configuración',
        backup: 'Respaldos',
        logs: 'Logs'
      }
    }
  };

  const roleColors = {
    'super-admin': 'danger',
    'admin': 'warning',
    'sub-admin': 'info',
    'socio': 'success'
  };

  const roleNames = {
    'super-admin': 'Super Administrador',
    'admin': 'Administrador',
    'sub-admin': 'Sub-Administrador',
    'socio': 'Socio'
  };

  useEffect(() => {
    if (!isSuperAdmin()) {
      toast.error('No tienes permisos para acceder a esta página');
      return;
    }
    fetchUsers();
  }, [isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = (user) => {
    setSelectedUser(user);
    setPermissions(user.permissions || {});
    setShowModal(true);
  };

  const handlePermissionChange = (resource, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [action]: value
      }
    }));
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`/api/users/${selectedUser._id}/permissions`, {
        permissions
      });
      
      toast.success('Permisos actualizados correctamente');
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Error al actualizar permisos');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`/api/users/${userId}/role`, { role: newRole });
      toast.success('Rol actualizado correctamente');
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Error al actualizar rol');
    }
  };

  if (!isSuperAdmin()) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          No tienes permisos para acceder a esta página.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <i className="fas fa-shield-alt me-2"></i>
                Gestión de Permisos
              </h4>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Permisos</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-user-circle fa-2x text-muted me-2"></i>
                          <div>
                            <div className="fw-bold">{user.name}</div>
                            <small className="text-muted">
                              Creado: {new Date(user.createdAt).toLocaleDateString()}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg={roleColors[user.role] || 'secondary'}>
                          {roleNames[user.role] || user.role}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={user.isActive ? 'success' : 'danger'}>
                          {user.isActive ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {user.role === 'super-admin' ? (
                            <Badge bg="danger">Todos los permisos</Badge>
                          ) : (
                            Object.entries(user.permissions || {}).map(([resource, actions]) => (
                              Object.entries(actions).filter(([, value]) => value).map(([action]) => (
                                <Badge key={`${resource}-${action}`} bg="info" className="small">
                                  {resources[resource]?.name}: {resources[resource]?.actions[action]}
                                </Badge>
                              ))
                            )).flat()
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleEditPermissions(user)}
                            disabled={user.role === 'super-admin'}
                          >
                            <i className="fas fa-edit me-1"></i>
                            Permisos
                          </Button>
                          
                          <Form.Select
                            size="sm"
                            value={user.role}
                            onChange={(e) => handleRoleChange(user._id, e.target.value)}
                            style={{ width: 'auto' }}
                            disabled={user.role === 'super-admin'}
                          >
                            <option value="socio">Socio</option>
                            <option value="sub-admin">Sub-Admin</option>
                            <option value="admin">Admin</option>
                            <option value="super-admin">Super Admin</option>
                          </Form.Select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para editar permisos */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-shield-alt me-2"></i>
            Editar Permisos - {selectedUser?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                Configurando permisos para: <strong>{selectedUser.name}</strong> ({roleNames[selectedUser.role]})
              </Alert>

              {Object.entries(resources).map(([resourceKey, resource]) => (
                <Card key={resourceKey} className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">{resource.name}</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {Object.entries(resource.actions).map(([actionKey, actionName]) => (
                        <Col md={6} key={actionKey} className="mb-2">
                          <Form.Check
                            type="checkbox"
                            id={`${resourceKey}-${actionKey}`}
                            label={actionName}
                            checked={permissions[resourceKey]?.[actionKey] || false}
                            onChange={(e) => handlePermissionChange(resourceKey, actionKey, e.target.checked)}
                          />
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSavePermissions}>
            <i className="fas fa-save me-1"></i>
            Guardar Permisos
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PermissionManagement;